import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormField,
  Input,
  Modal,
  Pagination,
  ProgressBar,
  Select,
  SelectProps,
  SpaceBetween,
  Table } from '@amzn/awsui-components-react';
import * as APIt from '../API';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { setReaderMode } from 'src/utils/DevicesUtils';
import { useSelector } from 'react-redux';
import { selectUsername, selectUserSelectedSite } from '../stores/slices/userSlice';
import { debug, validEmailAddresses } from '../utils/commonUtils';

function EmptyState(props: any) {
  return (
    <Box textAlign="center" color="inherit">
      <Box variant="strong" textAlign="center" color="inherit">
        {props.title}
      </Box>
      <Box variant="p" padding={{ bottom: 's' }} color="inherit">
        {props.subtitle}
      </Box>
      {props.action}
    </Box>
  );
}

enum SetReaderModeStatuses {
  notStarted = 'Not Started',
  inProgress = 'In Progress',
  failure = 'Failure',
  submitted = 'Submitted',
  updated = 'Updated'
}

interface ChangeModeInterface {
  changeModeVisible: boolean;
  emailAddresses: string;
  modes: SelectProps.Options;
  setChangeModeVisibleCallback(mode: boolean): void;
  singleSelection?: { device: APIt.ChildDevice, selection: { label: string, value: string } };
  selectedDevices?: APIt.ChildDevice;
  totalDevicesCount: number;
  updateDeviceCallback(updatedDevice: APIt.ChildDevice): void;
}

// TODO: Use ChangeModeInterface 
export default function ChangeMode(props: any) {
  debug(`ChangeMode() props is ${JSON.stringify(props)}`);
              
  const [ emailAddresses, setEmailAddresses ] = useState(props.emailAddresses);
  const [ confirmed, setConfirmed ] = useState(false);
  const [ newMode, setNewMode ] = useState<any>(props.singleSelection?.selection);
  const [ disableSelectMode, setDisableSelectMode ] = useState<boolean>(false);
  const [ delay, setDelay ] = useState<any>({ label: 'None', value: '0'});
  const [ inProgress, setInProgress ] = useState<boolean>(false);
  const [ progress, setProgress ] = useState(0);
  const [ progressDescription, setProgressDescription ] = useState('');
  const [ selectedDevices, setSelectedDevices ] = useState(
    props.singleSelection ? [ { ...props.singleSelection.device, setReaderModeStatus: SetReaderModeStatuses.notStarted } ] :
    props.selectedDevices.map((sd: APIt.ChildDevice) => {
      return {
        ...sd,
        setReaderModeStatus: SetReaderModeStatuses.notStarted
      }
    }));
  const [ showProgress, setShowProgress ] = useState(false);

  const username = useSelector(selectUsername);
  const userSelectedSite = useSelector(selectUserSelectedSite);

  const setReaderModeCallbackSuccess = (result: any) => {
    console.log('ChangeMode() setReaderModeCallbackSuccess() result is ', result);
    console.log('ChangeMode() setReaderModeCallbackSuccess() selectedDevices is ', selectedDevices);
    const selectedDevicesCopy = [...selectedDevices];
    const updatedSelectedDeviceIndex = selectedDevicesCopy.findIndex((sd: any) => sd.DeviceName === result.devices[0].deviceName);
    console.log('ChangeStatus() setReaderModeCallbackSuccess() updatedSelectedDeviceIndex is ', updatedSelectedDeviceIndex);
    const updatedSelectedDevice = selectedDevicesCopy[updatedSelectedDeviceIndex];
    if (result.status === 200 && result.devices[0].status === 'Success') {
      updatedSelectedDevice.setReaderModeStatus = SetReaderModeStatuses.updated;
      updatedSelectedDevice.Mode = newMode.value;
      const updatedSelectedDeviceForCallback = {...updatedSelectedDevice};
      delete updatedSelectedDeviceForCallback.setReaderModeStatus;
      console.log('ChangeMode() setReaderModeCallbackSuccess() calling props.updateDeviceCallback() with ', updatedSelectedDeviceForCallback);
      props.updateDeviceCallback(updatedSelectedDeviceForCallback);
    } else {
      updatedSelectedDevice.setReaderModeStatus = SetReaderModeStatuses.failure;
    }
    console.log('ChangeMode() setReaderModeCallbackSuccess() updatedSelectedDevice is ', updatedSelectedDevice);
    selectedDevicesCopy.splice(updatedSelectedDeviceIndex,1,updatedSelectedDevice);
    console.log('ChangeStatus() setDeviceMaskCallbackSuccess() selectedDevicesCopy is ', selectedDevicesCopy);
    setSelectedDevices(selectedDevicesCopy);
  };

  const setReaderModeCallbackFailure = (result: any) => {
    console.log('ChangeMode() setReaderModeCallbackFailure() result is ', result);
    const selectedDevicesCopy = [...selectedDevices];
    const failedUpdateSelectedDeviceIndex = selectedDevicesCopy.findIndex((sd: any) => sd.DeviceName === result.devices[0].deviceName);
    const failedUpdateSelectedDevice = selectedDevicesCopy[failedUpdateSelectedDeviceIndex];
    failedUpdateSelectedDevice.setReaderModeStatus = SetReaderModeStatuses.failure;
    selectedDevicesCopy.splice(failedUpdateSelectedDeviceIndex,1,failedUpdateSelectedDeviceIndex);
    console.log('ChangeStatus() setDeviceMaskCallbackFailure() selectedDevicesCopy is ', selectedDevicesCopy);
    setSelectedDevices(selectedDevicesCopy);
  };

  const setReaderModeCallbackSuccessForDelay = (result: any) => {
    console.log('ChangeStatus() setReaderModeCallbackSuccessForDelay() result is ', result);
    console.log('ChangeStatus() setReaderModeCallbackSuccessForDelay() selectedDevices is ', selectedDevices);
    const selectedDevicesCopy = [...selectedDevices];
    for (let result_device of result.devices) {
      console.log('ChangeStatus() setReaderModeCallbackSuccessForDelay() result_device is ', result_device);
      const updatedSelectedDeviceIndex = selectedDevicesCopy.findIndex((sd: any) => sd.DeviceName === result_device.deviceName);
      console.log('ChangeStatus() setReaderModeCallbackSuccessForDelay() updatedSelectedDeviceIndex is ', updatedSelectedDeviceIndex);
      const updatedSelectedDevice = selectedDevicesCopy[updatedSelectedDeviceIndex];
      if (result.status === 202) updatedSelectedDevice.setReaderModeStatus = SetReaderModeStatuses.submitted;
      if (result.status !== 202) updatedSelectedDevice.setReaderModeStatus = SetReaderModeStatuses.failure;
      console.log('ChangeStatus() setReaderModeCallbackSuccessForDelay() updatedSelectedDevice is ', updatedSelectedDevice);
      selectedDevicesCopy.splice(updatedSelectedDeviceIndex,1,updatedSelectedDevice);
      console.log('ChangeStatus() setReaderModeCallbackSuccessForDelay() selectedDevicesCopy is ', selectedDevicesCopy);
    }
    setSelectedDevices(selectedDevicesCopy);
    console.log('ChangeStatus() setReaderModeCallbackSuccessForDelay() done with call to setSelectedDevices using ', selectedDevicesCopy);
    setProgressDescription(`Complete: Submitted Request ${result.requestUUID}`);
    setProgress(100);
    setInProgress(false);
  };

  const setReaderModeCallbackFailureForDelay = (result: any) => {
    console.log('ChangeStatus() setReaderModeCallbackFailureForDelay() result is ', result);
    const selectedDevicesCopy = [...selectedDevices];
    for (let result_device of result.devices) {
      const failedUpdateSelectedDeviceIndex = selectedDevicesCopy.findIndex((sd: any) => sd.DeviceName === result_device.deviceName);
      const failedUpdateSelectedDevice = selectedDevicesCopy[failedUpdateSelectedDeviceIndex];
      failedUpdateSelectedDevice.setReaderModeStatus = SetReaderModeStatuses.failure;
      selectedDevicesCopy.splice(failedUpdateSelectedDeviceIndex,1,failedUpdateSelectedDevice);
    }
    console.log('ChangeStatus() setReaderModeCallbackFailureForDelay() selectedDevicesCopy is ', selectedDevicesCopy);
    setSelectedDevices(selectedDevicesCopy);
    setProgressDescription(`Failure: Submitted Request ${result.requestUUID}`);
    setProgress(100);
    setInProgress(false);
  };

  const processDevices = () => {
    if (inProgress) return;
    console.log('ChangeMode() processDevices()');
    let updatedSelectedDevices: APIt.ChildDevice[] = [];
    const selectedDevicesCopy = [...selectedDevices];
    console.log('ChangeMode() processDevices() selectedDevicesCopy.length is ', selectedDevicesCopy.length);
    console.log('ChangeMode() processDevices() selectedDevicesCopy is ', selectedDevicesCopy);
    console.log('ChangeMode() processDevices() delay.value is ', delay.value);
    if (delay.value > 0) {
      for (let i = 0; i < selectedDevicesCopy.length; i++) { 
        const updatedSelectedDevice = selectedDevicesCopy[i];
        updatedSelectedDevice.setReaderModeStatus = SetReaderModeStatuses.inProgress;
        updatedSelectedDevices = [...selectedDevicesCopy.filter((sd: APIt.ChildDevice) => sd.DeviceName !== updatedSelectedDevice.DeviceName), updatedSelectedDevice];
      }
      const devices = selectedDevicesCopy.map((sd: any) => { return {deviceName: sd.DeviceName, deviceHref: sd.device_href} } );
      console.log('ChangeStatus() processDevices() devices is ', devices);
      setReaderMode(
        selectedDevices[0].devicesource,
        devices,
        emailAddresses,
        newMode.value,
        delay.value*60,
        username,
        userSelectedSite.sitename,
        userSelectedSite.siteRegion)
        .then(setReaderModeCallbackSuccessForDelay, setReaderModeCallbackFailureForDelay);
    } else {
      for (let i = 0; i < selectedDevices.length; i++) {
        if (!selectedDevices[i].DeviceName || !selectedDevices[i].devicesource) continue;
        const deviceHref = selectedDevices[i].device_href === 'Not_Available' ? undefined : selectedDevices[i].device_href;
        const devices = [{deviceName: selectedDevices[i].DeviceName, deviceHref: deviceHref}];
        setReaderMode(selectedDevices[i].devicesource!, devices, '', newMode.value, 0, username, userSelectedSite.sitename, userSelectedSite.siteRegion)
          .then(setReaderModeCallbackSuccess, setReaderModeCallbackFailure);
        const updatedSelectedDevice = selectedDevices[i];
        updatedSelectedDevice.setReaderModeStatus = SetReaderModeStatuses.inProgress;
        updatedSelectedDevices = [...selectedDevices.filter((sd: APIt.ChildDevice) => sd.DeviceName !== updatedSelectedDevice.DeviceName), updatedSelectedDevice];
      }
      console.log('ChangeMode() processDevices() updatedSelectedDevices is ', updatedSelectedDevices);
      setSelectedDevices(updatedSelectedDevices);
    }
  };

  const confirmBtnHandler = () => {
    console.log('ChangeMode() confirmBtnHandler()');
    setDisableSelectMode(true);
    setProgress(0);
    setInProgress(true);
    setConfirmed(true);
    setShowProgress(true);
    processDevices();
  };
 
  useEffect(() => {
    if (progress >= 100 || delay.value > 0) return;
    debug(`ChangeMode() useEffect() [showProgress, selectedDevices] progress is ${progress} showProgress is ${showProgress}`);
    debug(`ChangeMode() useEffect() [showProgress, selectedDevices] selectedDevices is ${JSON.stringify(selectedDevices)}`);
    if (showProgress) {
      debug(`ChangeMode() useEffect() [showProgress, selectedDevices] selectedDevices.length is ${selectedDevices.length}`);
      let processedCount = 0;
      for (let i = 0; i < selectedDevices.length ; i++) {
        if (selectedDevices[i].setReaderModeStatus === SetReaderModeStatuses.updated ||
        selectedDevices[i].setReaderModeStatus === SetReaderModeStatuses.failure ||
        selectedDevices[i].setReaderModeStatus === SetReaderModeStatuses.submitted)
          processedCount++;
      }
      debug(`ChangeMode() useEffect() [showProgress, selectedDevices] processedCount is ${processedCount}`);
      setProgress(Math.ceil(processedCount/selectedDevices.length*100));
      const remaining = selectedDevices.length - processedCount;
      if (remaining === 0)
        setProgressDescription('Complete');
      else
        setProgressDescription(`${remaining} Devices Remaining...`);
    }
  }, [showProgress, selectedDevices]);

  useEffect(() => {
    if (progress < 100 || delay.value > 0) return;
    debug(`ChangeMode() useEffect() [progress] progress is ${progress}`);
    const selectedDevicesCopy = [...selectedDevices];
    selectedDevicesCopy.sort((a, b) => {
      if (b.setReaderModeStatus !== SetReaderModeStatuses.failure && a.setReaderModeStatus === SetReaderModeStatuses.failure) return 0;
      return 1;
    });
    debug(`ChangeMode() useEffect() [progress] selectedDevicesCopy is ${JSON.stringify(selectedDevicesCopy)}`);
    setSelectedDevices(selectedDevicesCopy);
  }, [progress]);

  const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(
    selectedDevices,
    {
      filtering: {
        empty: (
          <EmptyState
            title="No devices"
            subtitle="No devices to display."
          />
        ),
        noMatch: (
          <EmptyState
            title="No matches"
            subtitle="We can’t find a match."
            action={<Button onClick={() => actions.setFiltering('')}>Clear filter</Button>}
          />
        ),
      },
      pagination: { pageSize: 5 },
      sorting: {},
      selection: {},
    }
  );

  const filterModes = (): SelectProps.Options => {
    const options = props.modes.filter((m: SelectProps.Option) =>
      m.value !== 'Facility Code' &&
      m.value !== 'Unknown');
    return options;
  };

  const filteredModes = filterModes();

  const paginationLabels = {
    nextPageLabel: 'Next page',
    pageLabel: (pageNumber: number) => `Go to page ${pageNumber}`,
    previousPageLabel: 'Previous page'
  };

  useEffect(() => {
    // automatically confirm for single device selection
    debug(`ChangeMode() useEffect() [] props.singleSelection is ${JSON.stringify(props.singleSelection)} newMode is ${JSON.stringify(newMode)} items.length is ${items.length}`);
    if (props.singleSelection && newMode && items.length > 0) confirmBtnHandler();
  }, [])
              
  return (
    <Modal
      footer={
        <Box float={'right'}>
          <Button
            disabled={!newMode?.value || showProgress && progress < 100 || (delay.valuee > 0 && !validEmailAddresses(emailAddresses))}
            onClick={() => {
              if (progress === 0) confirmBtnHandler();
              if (progress >= 100) props.setChangeModeVisibleCallback(false);
            }}
            variant="primary"
          >
            {progress < 100 ? 'Confirm' : 'OK'}
          </Button>
        </Box>
      }
      header={`Change Mode (${props.singleSelection ? 1 : props.selectedDevices.length}/${props.totalDevicesCount})`}
      onDismiss={() => props.setChangeModeVisibleCallback(false)}
      size="large"
      visible={props.changeModeVisible}
    >
      <Table
        {...collectionProps}
        trackBy='DeviceName'
        header={
          <>
          {
            showProgress &&
            <ProgressBar
              status="in-progress"
              value={progress}
              additionalInfo=""
              description={progressDescription}
              label={`Updating Device Mode to ${newMode.label}`}
            />
          }
          <SpaceBetween size="xs" direction="horizontal">
            <FormField label='New Mode'>
              <Select
                disabled={disableSelectMode}
                invalid={!newMode}
                onChange={({ detail }) => {
                  setNewMode(detail.selectedOption);
                  setShowProgress(false);
                }}
                options={filteredModes}
                placeholder="Select a Mode"
                selectedAriaLabel="Selected"
                selectedOption={ props.singleSelection ? props.singleSelection.selection : newMode }
              />
            </FormField>
            <FormField label="Delay">
              <Select
                disabled={confirmed}
                expandToViewport
                onChange={({ detail }) => {
                  setDelay(detail.selectedOption)
                }}
                options={[
                  { label: 'None', value: '0' },
                  { label: '15 seconds', value: '.25' },
                  { label: '30 seconds', value: '.5' },
                  { label: '1 minute', value: '1' },
                  { label: '2 minutes', value: '2' },
                  { label: '3 minutes', value: '3' },
                  { label: '4 minutes', value: '4' },
                  { label: '5 minutes', value: '5' },
                ]}
                selectedAriaLabel="Selected"
                selectedOption={ delay }
              />
            </FormField>
            {delay.value != 0 && 
            <FormField label='eMail Addresses'>
              <Input
                disabled={delay.value == 0 || confirmed}
                invalid={!emailAddresses}
                onChange={({ detail }) => setEmailAddresses(detail.value)}
                placeholder='email addresses'
                value={emailAddresses}
              />
            </FormField>
            }
          </SpaceBetween>
          </>
        }
        ariaLabels={{
          selectionGroupLabel: "Devices selection",
          allItemsSelectionLabel: ({ selectedItems }) =>
            `${selectedItems.length} ${
              selectedItems.length === 1 ? "item" : "items"
            } selected`,
          itemSelectionLabel: ({ selectedItems }, item: any) => {
            const isItemSelected = selectedItems.filter(
            (i: any) => i.name === item.name
          ).length;
          return `${item.name} is ${
          isItemSelected ? "" : "not"
            } selected`;
        }
        }}
        columnDefinitions={[
          {
            id: "deviceName",
            header: "Device Name",
            cell: (e: any) => e.DeviceName,
            sortingField: "DeviceName"
          },
          {
            id: "mode",
            header: "Mode",
            cell: (e: any) => e.Mode,
            sortingField: "Mode"
          },
          {
            id: "setReaderModeStatus",
            header: "Status",
            cell: (e: any) => {
              switch(e.setReaderModeStatus) {
                case SetReaderModeStatuses.updated:
                  return <span style={{color: 'green'}}>{e.setReaderModeStatus}</span>
                case SetReaderModeStatuses.failure:
                  return <span style={{color: 'red'}}>{e.setReaderModeStatus}</span>
                case SetReaderModeStatuses.submitted:
                  return <span style={{color: 'green'}}>{e.setReaderModeStatus}</span>
                default:
                  return e.setReaderModeStatus
              }
            },
            sortingField: "setReaderModeStatus"
          },
        ]}
        items={items}
        visibleColumns={[
          'deviceName',
          'mode',
          'setReaderModeStatus'
        ]}
        pagination={
          <Pagination
            {...paginationProps}
            ariaLabels={paginationLabels}
          />
        }
      />
    </Modal>
  )
}